import { getCustomProps } from '../forms'
import { getData } from '../get-table'
import { HttpService } from '../http.service'

const http = new HttpService()

function normalizeOptionalNumber(value: unknown): number | null {
  if (value == null || value === '') return null
  return typeof value === 'number' ? value : Number(value)
}

export interface Loan {
  payrollId: string
  typeLoansId: string
  conceptId: string
  employeeId: string
  currencyId: string
  typeRateId: string
  amount: number
  numberOfQuotas: number
  typeInterestId: string
  fixedAmount: boolean
  quotaAmount: number
  loanPaid: boolean
  percentageInterest: number
  exchangeRate: number
  balance: number
  startDate: Date
  endDate: Date
  lastPaymentDate: Date
  installmentsPending: number
  onePeriod: boolean
  twoPeriod: boolean
  threePeriod: boolean
  fourPeriod: boolean
  lastPeriod: boolean
  observation: string
  payments: []
}

export async function getLoans(
  offset: number,
  perPage: number,
  dataOrder: string,
  search: string = ''
): Promise<any> {
  // where's
  const where = ['loans.payroll_id = :payrollId']
  if (search) {
    where.push(
      `CONCAT(loan_types.name,concepts.name,CONCAT(employees.code,' - ', employees.name)) ILIKE '%${search}%'`
    )
  }
  // order by
  let modifier = []
  dataOrder !== ''
    ? (modifier = [`order by ${dataOrder[1]} ${dataOrder[0]}`])
    : (modifier = ['order by loans.updated_at desc'])
  // result
  const response: any = await getData({
    tables: ['pg_loans'],
    fields: [
      'loans.id as id',
      'payrolls.name as payrollName',
      'loan_types.name as loanType',
      'concepts.name as conceptName',
      "CONCAT(employees.code,' - ', employees.name) as employeeName",
      'currencies.name as currencyName',
      'loans.start_date',
      'loans.is_loan_paid',
      'loans.is_inactive',
      'loans.updated_at'
    ],
    join: [
      'INNER JOIN loan_types ON loans.loan_type_id=loan_types.id',
      'INNER JOIN concepts ON loans.concept_id=concepts.id',
      'INNER JOIN employees ON loans.employee_id=employees.id',
      'INNER JOIN payrolls ON loans.payroll_id=payrolls.id',
      'LEFT JOIN currencies ON loans.currency_id=currencies.id'
    ],
    where,
    modifier,
    options: {
      outputFormat: 'json',
      pagination: { offset, limit: perPage }
    }
  })
  return response
}

export async function getLoan(id: string): Promise<Loan> {
  const data = await getData<any>({
    tables: ['pg_loans'],
    join: [
      //  'INNER JOIN managements ON  managements.id = departments.management_id'
    ],
    fields: [
      'loans.id',
      'payroll_id as payrollId',
      'loan_type_id as typeLoansId',
      'concept_id as conceptId',
      'employee_id as employeeId',
      'currency_id as currencyId',
      'amount',
      'number_quotas as numberOfQuotas',
      'interest_type_id as typeInterestId',
      'rate_type_id as typeRateId',
      'is_fixed_amount as fixedAmount',
      'quota_amount as quotaAmount',
      'is_loan_paid as loanPaid',
      'percentage_interest as percentageInterest',
      'exchange_rate as exchangeRate',
      'balance',
      "TO_CHAR(start_date,'yyyy-mm-dd') as startDate",
      "TO_CHAR(end_date,'yyyy-mm-dd') as endDate",
      "TO_CHAR(last_payment_date,'yyyy-mm-dd') as lastPaymentDate",
      'installments_pending as installmentsPending',
      'one_period as onePeriod',
      'two_period as twoPeriod',
      'three_period as threePeriod',
      'four_period as fourPeriod',
      'last_period as lastPeriod',
      'observation',
      'custom_fields as custom'
    ],
    where: [`loans.id = '${id}'`],
    options: {
      outputFormat: 'json',
      pagination: { offset: 0, limit: 10 }
    }
  })
  return data[0].results[0]
}

export async function createLoan(data: Loan): Promise<boolean> {
  const response: any = await http.post('loans/loan', {
    payrollId: data.payrollId,
    typeLoansId: data.typeLoansId,
    conceptId: data.conceptId,
    employeeId: data.employeeId,
    currencyId: data.currencyId,
    typeRateId: data.typeRateId,
    amount: data.amount,
    numberOfQuotas: data.numberOfQuotas,
    typeInterestId: data.typeInterestId,
    fixedAmount: data.fixedAmount,
    quotaAmount: data.quotaAmount,
    loanPaid: data.loanPaid,
    percentageInterest: data.percentageInterest,
    exchangeRate: data.exchangeRate,
    balance: data.balance,
    startDate: data.startDate,
    endDate: data.endDate,
    lastPaymentDate: data.lastPaymentDate,
    installmentsPending: normalizeOptionalNumber(data.installmentsPending),
    onePeriod: data.onePeriod,
    twoPeriod: data.twoPeriod,
    threePeriod: data.threePeriod,
    fourPeriod: data.fourPeriod,
    lastPeriod: data.lastPeriod,
    observation: data.observation,
    payments: data.payments,
    customFields: {
      ...getCustomProps(data)
    }
  })

  if (response?.data?.data?.error) {
    return false
  }
  return true
}

export async function updateLoan(data: Loan, id: string): Promise<boolean> {
  const response: any = await http.put('loans/loan/' + id, {
    payrollId: data.payrollId,
    typeLoansId: data.typeLoansId,
    conceptId: data.conceptId,
    employeeId: data.employeeId,
    currencyId: data.currencyId,
    typeRateId: data.typeRateId,
    amount: data.amount,
    numberOfQuotas: data.numberOfQuotas,
    typeInterestId: data.typeInterestId,
    fixedAmount: data.fixedAmount,
    quotaAmount: data.quotaAmount,
    loanPaid: data.loanPaid,
    percentageInterest: data.percentageInterest,
    exchangeRate: data.exchangeRate,
    balance: data.balance,
    startDate: data.startDate,
    endDate: data.endDate,
    lastPaymentDate: data.lastPaymentDate,
    installmentsPending: normalizeOptionalNumber(data.installmentsPending),
    onePeriod: data.onePeriod,
    twoPeriod: data.twoPeriod,
    threePeriod: data.threePeriod,
    fourPeriod: data.fourPeriod,
    lastPeriod: data.lastPeriod,
    observation: data.observation,
    payments: data.payments,
    customFields: {
      ...getCustomProps(data)
    }
  })

  if (response?.data?.data?.error) {
    return false
  }
  return true
}

export async function updateStateLoan(
  id: string,
  enable: boolean
): Promise<boolean> {
  const response: any = await http.put(`loans/loan/${id}/enable`, {
    enable
  })

  if (response?.data?.data?.error) {
    return false
  }
  return true
}

export async function deleteLoan(id: string): Promise<boolean> {
  const response: any = await http.delete('loans/loan/' + id)

  if (response?.data?.data?.error) {
    return false
  }
  return true
}
